import MockAdapter from 'axios-mock-adapter';

import { API_MOCK_ENABLED } from '../config/api';
import beneficiaries from './beneficiaries.json';
import bulkBeneficiaries from './bulk-beneficiaries.json';
import countries from './countries.json';
import currency from './currencies.json';
import disputes from './disputes.json';
import kyc from './kyc.json';
import payment_links from './payment-links.json';
import payoutReceiveWallets from './payout-receive-wallets.json';
import payoutWallets from './payout-wallets.json';
import payouts from './payouts.json';
import refundPreview from './refund-preview.json';
import account from './settlement-accounts.json';
import settlements from './settlements.json';
import transactionDetails from './transaction-details.json';
import transactions from './transactions.json';
import wallets from './wallets.json';

function parseQueryString(url) {
  const queryString = url.replace(/.*\?/, '');

  if (queryString === url || !queryString) {
    return null;
  }

  const urlParams = new URLSearchParams(queryString);
  const result = {};

  urlParams.forEach((val, key) => {
    if (result[key]) {
      result[key] = [result[key], val];
    } else {
      result[key] = val;
    }
  });

  return result;
}

export const isMockEnabled = () => {
  return API_MOCK_ENABLED || false;
};

export const initializeAxiosMockAdapter = (instance) => {
  const mock = new MockAdapter(instance);
  mock.onPost('auth/login').reply((config) => getToken(config));
  mock.onPost('auth/signup').reply(() => createAccount());
  mock.onPost('auth/create-account').reply((config) => signupV2(config));
  mock.onPost('auth/create-account/verify-otp').reply((config) => verifyOTP(config));
  mock.onPost('auth/create-account/resend-otp').reply((config) => resendOTP(config));
  mock.onPost('auth/setup').reply((config) => accountSetup(config));
  mock.onPost('payout/initiate_single_payout').reply((config) => singlePayout(config));
  mock.onPatch('auth/password/update').reply((config) => updatePassword(config));
  mock.onPost('auth/twofa/verify').reply((config) => verify2faCode(config));
  mock.onGet('settlement/account').reply(() => getItem('accounts'));
  mock.onGet(/payout\/transaction\/?.*/).reply((config) => getPayouts(config));
  mock.onGet(/dashboard\/transaction\/[^/?]+$/).reply((config) => getTransactionDetails(config));
  mock.onGet(/transaction\/?.*/).reply((config) => getTransactions(config));
  mock.onGet(/settlement\/?.*/).reply((config) => getSettlements(config));
  mock
    .onPost(/refunds\/transactions\/[^/]+\/preview$/)
    .reply((config) => previewRefundMock(config));
  mock.onPost(/refunds\/transactions\/[^/]+$/).reply((config) => createRefundMock(config));
  mock.onPost(/disputes\/[^/]+\/accept$/).reply((config) => acceptDisputeMock(config));
  mock.onGet(/disputes\/?.*/).reply((config) => getDisputes(config));
  mock.onGet('wallet').reply(() => getItem('wallets'));
  mock.onGet('user').reply(() => getItem('user'));
  mock.onGet('country').reply(() => getItem('countries'));
  mock.onGet(/currency\/?.*/).reply(() => getItem('currency'));
  mock.onGet(/paymentlinks\/?.*/).reply(() => getItem('paymentlinks'));
  mock.onGet('payout/wallet').reply(() => getItem('payout-wallets'));
  mock.onGet('payout/wallet_address').reply(() => getItem('payout-receive-wallets'));
  mock.onGet(/payout\/beneficiary\/fetchAll\/?.*/).reply(() => getItem('beneficiaries'));
  mock.onGet('/payout/beneficiary?grouped=true').reply(() => getItem('bulk-beneficiaries'));
  mock.onPost('/payout/beneficiary/bulk').reply(() => uploadBeneficiaries());
  mock.onGet('/kyc/check').reply(() => getKycItem('check'));
  mock.onGet('/kyc/docs?kyc_type=FREELANCER').reply(() => getKycItem('docs', 'FREELANCER'));

  // ── Wallet conversions (dashboard surface) ─────────────────────────────────
  // POST exact-match for both, regex for the GET so it accepts ?limit&offset...
  mock
    .onPost('dashboard/wallets/conversions/quotes')
    .reply((config) => createConversionQuoteMock(config));
  mock
    .onPost('dashboard/wallets/conversions')
    .reply((config) => createWalletConversionMock(config));
  mock
    .onGet(/dashboard\/wallets\/conversions(\?.*)?$/)
    .reply((config) => listWalletConversionsMock(config));
};

const token = 'test-jwt-token';
const merchantUser = {
  firstName: 'Merchant',
  lastName: 'User',
  role: 'Merchant',
  email: 'test@basqet.com',
  twofa_enabled: true
};
const adminUser = {
  firstName: 'Admin',
  lastName: 'User',
  role: 'Admin',
  email: 'admin@basqet.com'
};

export const getToken = (config) => {
  const { email, password } = JSON.parse(config.data);

  if (email === 'admin@basqet.com' && password === 'Pass1020@') {
    return [200, { data: { accessToken: token, admin: adminUser, merchantUser } }];
  }
  if (email === 'test@basqet.com' && password === 'Pass1020@') {
    return [200, { data: { accessToken: token, merchantUser, admin: adminUser } }];
  }
  return [400, { message: 'Invalid login details', data: { email: 'required' } }];
};

export const createAccount = () => {
  return [
    200,
    {
      message: 'Account created successfully',
      data: { accessToken: token, merchantUser, admin: adminUser }
    }
  ];
};

export const accountSetup = (config) => {
  const { businessName } = JSON.parse(config.data);
  if (businessName.includes('error')) {
    return [400, { message: 'Error while setting up account' }];
  }
  return [200, { data: { token, merchantUser, admin: adminUser } }];
};
export const updatePassword = () => {
  return [200, { data: { message: 'Password changed successfully' } }];
};
export const singlePayout = (config) => {
  const { address, amount, currency_id, network } = JSON.parse(config.data);
  if (!address || !amount || !currency_id || !network) {
    return [400, { message: 'Form error' }];
  }
  return [200, { data: { message: 'Payout successfull' } }];
};

export const getTransactions = (config) => {
  const { page, search, status } = parseQueryString(config.url);
  let results = transactions;
  if (search) {
    results = transactions.filter((item) => JSON.stringify(item).includes(search));
  }
  if (status) {
    results = transactions.filter((item) => item.status === status);
  }
  return [200, { data: results, totalPages: search || status ? 1 : 5, page: Number(page) }];
};

export const getTransactionDetails = (config) => {
  const transactionId = decodeURIComponent(config.url.split('/').pop());
  return [
    200,
    {
      data: {
        ...transactionDetails,
        summary: { ...transactionDetails.summary, basqet_transaction_id: transactionId }
      }
    }
  ];
};

export const getSettlements = (config) => {
  const { page, search, status } = parseQueryString(config.url);
  let results = settlements;
  if (search) {
    results = settlements.filter((item) => JSON.stringify(item).includes(search));
  }
  if (status) {
    results = settlements.filter((item) => item.status === status);
  }
  return [200, { data: results, totalPages: search || status ? 1 : 5, page: Number(page) }];
};
export const getDisputes = (config) => {
  const { page, search, status, reference } = parseQueryString(config.url);
  let results = disputes;
  if (search) {
    results = disputes.filter((item) => JSON.stringify(item).includes(search));
  }
  if (status) {
    results = disputes.filter((item) => item.status === status);
  }

  if (reference) {
    results = disputes.filter((item) => item.reference === reference);
  }
  return [200, { data: results, totalPages: search || status ? 1 : 5, page: Number(page) }];
};

// ── Refunds / dispute resolution (BS-191) ──────────────────────────────────
export const previewRefundMock = (config) => {
  const body = JSON.parse(config.data || '{}');
  if ((body.wallet_address || '').toLowerCase().includes('invalid')) {
    return [422, { message: 'The provided wallet address is not valid' }];
  }
  const preview = {
    ...refundPreview,
    refund_type: body.refund_type || refundPreview.refund_type,
    wallet_address: body.wallet_address || null,
    wallet_network: body.wallet_network || refundPreview.wallet_network
  };
  return [200, { data: preview }];
};

export const createRefundMock = (config) => {
  const body = JSON.parse(config.data || '{}');
  if (!body.otp || body.otp.length !== 6) {
    return [400, { message: 'Invalid code' }];
  }
  if ((body.wallet_address || '').toLowerCase().includes('fail')) {
    return [500, { message: 'Unable to process refund' }];
  }
  const reference = (config.url.match(/transactions\/([^/?]+)/) || [])[1] || 'unknown';
  return [
    201,
    {
      data: {
        id: 9001,
        reference: `rf_${reference}`,
        transaction_reference: reference,
        dispute_id: 1,
        refund_type: body.refund_type,
        status: 'PENDING',
        payout_currency: refundPreview.payout_currency,
        payout_amount: refundPreview.payout_amount,
        wallet_address: body.wallet_address,
        wallet_network: body.wallet_network || null,
        reason: body.reason || null,
        is_live: true,
        created_at: new Date().toISOString()
      }
    }
  ];
};

export const acceptDisputeMock = (config) => {
  const body = JSON.parse(config.data || '{}');
  if (!body.otp || body.otp.length !== 6) {
    return [400, { message: 'Invalid code' }];
  }
  return [
    201,
    {
      data: {
        id: 8001,
        reference: 'dr_mockref',
        dispute_id: 1,
        transaction_id: 2232,
        resolution_action: body.action,
        status: 'PROCESSING',
        expected_amount: '478392',
        actual_paid_amount: '778392',
        difference_amount: '300000',
        is_live: true,
        created_at: new Date().toISOString()
      }
    }
  ];
};

export const getPayouts = (config) => {
  const { page, search, status } = parseQueryString(config.url);
  let results = payouts;
  if (search) {
    results = payouts.filter((item) => JSON.stringify(item).includes(search));
  }
  if (status) {
    results = payouts.filter((item) => item.status === status);
  }
  return [200, { data: results, totalPages: search || status ? 1 : 5, page: Number(page) }];
};

export const uploadBeneficiaries = () => {
  return [
    200,
    {
      status: 'success',
      data: [
        {
          label: 'binance',
          row_count: 3,
          currency_id: 6,
          created_at: '2022-07-28T05:59:10.000Z'
        }
      ],
      meta: {}
    }
  ];
};

const getKycItem = (endpoint, kyc_type) => {
  return [200, { data: kyc[endpoint]?.[kyc_type] }];
};

export const getItem = (itemType) => {
  switch (itemType) {
    case 'wallets':
      return [200, { data: wallets }];
    case 'countries':
      return [200, { data: countries }];
    case 'accounts':
      return [200, { data: account }];
    case 'currency':
      return [200, { data: currency }];
    case 'paymentlinks':
      return [200, { data: payment_links }];
    case 'payout-receive-wallets':
      return [200, { data: payoutReceiveWallets }];
    case 'payout-wallets':
      return [200, { data: payoutWallets }];
    case 'beneficiaries':
      return [200, { data: beneficiaries }];
    case 'bulk-beneficiaries':
      return [200, { data: bulkBeneficiaries }];
  }
};

export const verify2faCode = () => {
  return [200, { data: { accessToken: token, merchantUser, admin: adminUser } }];
};

const maskEmail = (email) => {
  const [local, domain] = email.split('@');
  const visibleCount = Math.min(4, Math.floor(local.length / 2));
  return `${local.slice(0, visibleCount)}****@${domain}`;
};

export const signupV2 = (config) => {
  const body = JSON.parse(config.data);
  if (!body.email || !body.password || !body.first_name || !body.last_name) {
    return [400, { status: 'error', message: 'Validation error', data: {} }];
  }
  return [
    200,
    {
      status: 'success',
      data: { message: 'OTP sent to your email address', email: maskEmail(body.email) }
    }
  ];
};

export const verifyOTP = (config) => {
  const { otp } = JSON.parse(config.data);
  if (otp && otp.length === 6) {
    return [
      201,
      {
        status: 'success',
        message: 'Account created successfully',
        data: { accessToken: token, merchantUser, admin: adminUser }
      }
    ];
  }
  return [400, { status: 'error', message: 'Invalid OTP' }];
};

export const resendOTP = (config) => {
  const { email } = JSON.parse(config.data);
  if (!email) {
    return [400, { status: 'error', message: 'Email is required' }];
  }
  return [
    200,
    {
      status: 'success',
      data: { message: 'OTP sent to your email address', email: maskEmail(email) }
    }
  ];
};

// ─── Wallet conversions mocks ──────────────────────────────────────────────
//
// Mirrors the backend's `/dashboard/wallets/conversions/*` envelope exactly
// (see basquet-backend src/api/v1/controllers/dashboardWalletConversion.controller.ts).
// Error envelope is `{ status: <CODE>, data: { message } }` — NOT the success
// envelope — because the controller branches on WalletConversionException.

const CONVERSION_RATES = {
  'BTC-NGN': 112000000,
  'NGN-BTC': 1 / 112000000,
  'BTC-USDT': 63500,
  'USDT-BTC': 1 / 63500,
  'NGN-USDT': 0.00071,
  'USDT-NGN': 1400,
  'XRP-NGN': 800,
  'NGN-XRP': 1 / 800,
  'QDX-NGN': 250,
  'NGN-QDX': 1 / 250
};

// Quote IDs are short-lived; per-process Map keyed by id. Each carries the
// pair/amount/expiry so create-conversion can validate + simulate errors.
const conversionQuotes = new Map();

const recentConversions = [
  {
    conversion_id: 'Conv_18420',
    rate_id: 'rate_18420',
    status: 'SUCCESS',
    from_currency: 'NGN',
    to_currency: 'USDT',
    from_amount: 14000,
    to_amount: 10,
    from_display: '14,000.00 NGN',
    to_display: '10.00 USDT',
    rate_used: 1400,
    reference: null,
    failure_reason: null,
    created_at: '2024-10-03T09:30:21.000Z'
  },
  {
    conversion_id: 'Conv_18419',
    rate_id: 'rate_18419',
    status: 'INITIATED',
    from_currency: 'USDT',
    to_currency: 'NGN',
    from_amount: 5,
    to_amount: 7000,
    from_display: '5.00 USDT',
    to_display: '7,000.00 NGN',
    rate_used: 1400,
    reference: null,
    failure_reason: null,
    created_at: '2024-10-02T09:30:21.000Z'
  },
  {
    conversion_id: 'Conv_18412',
    rate_id: 'rate_18412',
    status: 'FAILED',
    from_currency: 'USD',
    to_currency: 'USDT',
    from_amount: 2000,
    to_amount: 199.84,
    from_display: '2,000.00 USD',
    to_display: '199.84 USDT',
    rate_used: 1,
    reference: null,
    failure_reason: 'INSUFFICIENT_BALANCE',
    created_at: '2024-10-01T09:30:21.000Z'
  },
  {
    conversion_id: 'Conv_18408',
    rate_id: 'rate_18408',
    status: 'FAILED',
    from_currency: 'NGN',
    to_currency: 'USDT',
    from_amount: 50000,
    to_amount: 35.71,
    from_display: '50,000.00 NGN',
    to_display: '35.71 USDT',
    rate_used: 1400,
    reference: null,
    failure_reason: 'CONVERSION_FAILED',
    created_at: '2024-09-17T09:30:21.000Z'
  },
  {
    conversion_id: 'Conv_18401',
    rate_id: 'rate_18401',
    status: 'INITIATED',
    from_currency: 'BTC',
    to_currency: 'USDT',
    from_amount: 0.005,
    to_amount: 318.4,
    from_display: '0.00500000 BTC',
    to_display: '318.40 USDT',
    rate_used: 63680,
    reference: null,
    failure_reason: null,
    created_at: '2024-08-28T09:30:21.000Z'
  }
];

const conversionError = (status, code, message) => [status, { status: code, data: { message } }];

export const createConversionQuoteMock = (config) => {
  const body = JSON.parse(config.data || '{}');
  const fromCurrency = String(body.from_currency || '').toUpperCase();
  const toCurrency = String(body.to_currency || '').toUpperCase();
  const amount = Number(body.amount);

  if (!fromCurrency || !toCurrency) {
    return conversionError(400, 'INVALID_REQUEST', 'Missing currency');
  }
  if (fromCurrency === toCurrency) {
    return conversionError(400, 'INVALID_REQUEST', 'Source and destination must differ');
  }
  if (!Number.isFinite(amount) || amount <= 0) {
    return conversionError(400, 'INVALID_REQUEST', 'Amount must be greater than 0');
  }

  const pair = `${fromCurrency}-${toCurrency}`;
  const rate = CONVERSION_RATES[pair];
  if (!rate) {
    return conversionError(400, 'INVALID_REQUEST', `${fromCurrency}/${toCurrency} not supported`);
  }

  const quoteId = `quote_mock_${Date.now()}`;
  const toAmount = amount * rate;
  // Backend serves quotes with a short TTL; 10s mirrors the design countdown.
  const expiresAt = new Date(Date.now() + 10000).toISOString();

  const quote = {
    quote_id: quoteId,
    from_currency: fromCurrency,
    to_currency: toCurrency,
    from_amount: amount,
    to_amount: toAmount,
    exchange_rate: rate,
    expires_at: expiresAt
  };
  conversionQuotes.set(quoteId, quote);

  return [201, { status: 'success', data: quote }];
};

export const createWalletConversionMock = (config) => {
  const body = JSON.parse(config.data || '{}');
  const quoteId = body.quote_id;
  const reference = body.reference ?? null;

  if (!quoteId || typeof quoteId !== 'string') {
    return conversionError(400, 'INVALID_REQUEST', 'quote_id is required');
  }

  const quote = conversionQuotes.get(quoteId);
  if (!quote) {
    return conversionError(
      404,
      'QUOTE_NOT_FOUND',
      'Conversion quote does not exist or has expired'
    );
  }
  if (new Date(quote.expires_at).getTime() <= Date.now()) {
    conversionQuotes.delete(quoteId);
    return conversionError(400, 'QUOTE_EXPIRED', 'Conversion quote has expired');
  }

  // Test hook: a reference containing 'insufficient' triggers the balance error.
  if (typeof reference === 'string' && /insufficient/i.test(reference)) {
    return conversionError(400, 'INSUFFICIENT_BALANCE', 'Debit wallet balance too low');
  }
  if (typeof reference === 'string' && /fail/i.test(reference)) {
    return conversionError(500, 'CONVERSION_FAILED', 'Unexpected system error');
  }

  conversionQuotes.delete(quoteId);
  const conversionId = `Conv_${Date.now()}`;
  const conversion = {
    conversion_id: conversionId,
    rate_id: `rate_${conversionId}`,
    status: 'SUCCESS',
    from_currency: quote.from_currency,
    to_currency: quote.to_currency,
    from_amount: quote.from_amount,
    to_amount: quote.to_amount,
    from_display: `${quote.from_amount} ${quote.from_currency}`,
    to_display: `${quote.to_amount} ${quote.to_currency}`,
    rate_used: quote.exchange_rate,
    reference,
    failure_reason: null,
    created_at: new Date().toISOString()
  };
  recentConversions.unshift(conversion);
  return [201, { status: 'success', data: conversion }];
};

export const listWalletConversionsMock = (config) => {
  const params = parseQueryString(config.url) || {};
  const limit = Math.min(Math.max(parseInt(params.limit, 10) || 50, 1), 100);
  const offset = Math.max(parseInt(params.offset, 10) || 0, 0);
  const reference = params.reference;

  let results = recentConversions;
  if (reference) {
    const needle = String(reference).toLowerCase();
    results = results.filter((c) => (c.reference || '').toLowerCase().includes(needle));
  }

  // Backend overfetches by one to compute hasNext; mirror that.
  const slice = results.slice(offset, offset + limit + 1);
  const hasNext = slice.length > limit;
  const data = hasNext ? slice.slice(0, -1) : slice;
  return [
    200,
    {
      status: 'success',
      data,
      meta: { limit, hasNext, count: hasNext ? limit : data.length }
    }
  ];
};
